import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Bot, MessageSquare, Newspaper, LogOut, User, Building2, UserSquare, Key, Lock, Terminal, Search, FileText, Settings, ChevronDown, ChevronRight, List, Database, Box, Archive, File as FilePdf, Shield, Building, BarChart, Package } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, signOut } = useAuthStore();
  const [webResearchOpen, setWebResearchOpen] = useState(true);
  const [webExtractOpen, setWebExtractOpen] = useState(true);
  const [knowledgeBaseOpen, setKnowledgeBaseOpen] = useState(true);
  const [securityOpen, setSecurityOpen] = useState(true);
  const [organizationOpen, setOrganizationOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login', { replace: true });
    }
  };

  if (!currentUser) {
    return null;
  }

  const userName = currentUser.user_metadata?.name || currentUser.email?.split('@')[0] || 'Usuario';
  const userRole = currentUser.user_metadata?.role || 'usuario_estandar';
  const userGroup = currentUser.user_metadata?.grupo?.nombre || 'Sin grupo';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation */}
      <nav className="bg-blue-900 text-white shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <Link to="/admin" className="flex items-center">
                <Bot className="w-8 h-8 mr-2" />
                <span className="font-semibold text-xl">SYNEPSIA</span>
              </Link>
              
              {/* Active User */}
              <div className="flex items-center space-x-3 pl-6 border-l border-blue-800">
                <div className="bg-blue-800 rounded-full p-2">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-blue-200">{userGroup}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <Link 
                to="/admin"
                className={`text-sm ${location.pathname === '/admin' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
              >
                Panel Principal
              </Link>
              <Link 
                to="/admin/terminal"
                className={`text-sm ${location.pathname === '/admin/terminal' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
              >
                Terminal
              </Link>
              <Link 
                to="/admin/news"
                className={`text-sm ${location.pathname === '/admin/news' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
              >
                Noticias
              </Link>
              <Link 
                to="/admin/conversations"
                className={`text-sm ${location.pathname === '/admin/conversations' ? 'text-white' : 'text-gray-300 hover:text-white'}`}
              >
                Mis Conversaciones
              </Link>
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-300 hover:text-white ml-6 pl-6 border-l border-blue-800"
                title="Cerrar Sesión"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-purple-50 shadow-md min-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-3 border-b bg-white">
            <h5 className="font-semibold text-sm">Rol</h5>
            <p className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${
              userRole === 'administrador' 
                ? 'bg-purple-100 text-purple-800'
                : userRole === 'usuario_avanzado'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {userRole === 'administrador' 
                ? 'Administrador'
                : userRole === 'usuario_avanzado'
                ? 'Usuario Avanzado'
                : 'Usuario Estándar'}
            </p>
          </div>
          <nav className="p-2">
            <Link
              to="/admin"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Panel Principal
            </Link>
            <Link
              to="/admin/terminal"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/terminal'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Terminal className="w-4 h-4 mr-2" />
              Terminal
            </Link>

            {/* Analytics Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Analítica
            </div>

            <div className="mb-1">
              <button
                onClick={() => setAnalyticsOpen(!analyticsOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/empresa-mensajes-limites') || 
                  location.pathname.startsWith('/admin/tipos-suscripcion')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <BarChart className="w-4 h-4 mr-2" />
                  Analítica
                </div>
                {analyticsOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {analyticsOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/empresa-mensajes-limites"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/empresa-mensajes-limites'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <MessageSquare className="w-3 h-3 mr-2" />
                    Límites de Mensajes
                  </Link>
                  <Link
                    to="/admin/tipos-suscripcion"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tipos-suscripcion'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Package className="w-3 h-3 mr-2" />
                    Tipos de Suscripción
                  </Link>
                </div>
              )}
            </div>

            {/* Organization Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Organización
            </div>

            <div className="mb-1">
              <button
                onClick={() => setOrganizationOpen(!organizationOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/empresas')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-2" />
                  Organización
                </div>
                {organizationOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {organizationOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/empresas"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/empresas'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Building2 className="w-3 h-3 mr-2" />
                    Empresas
                  </Link>
                </div>
              )}
            </div>

            {/* Security Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Seguridad de Acceso
            </div>

            <div className="mb-1">
              <button
                onClick={() => setSecurityOpen(!securityOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/security')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  Control de Acceso
                </div>
                {securityOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {securityOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/funcionalidades"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/funcionalidades'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <List className="w-3 h-3 mr-2" />
                    Funcionalidades
                  </Link>
                  <Link
                    to="/admin/funcionalidades-roles"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/funcionalidades-roles'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Lock className="w-3 h-3 mr-2" />
                    Roles y Permisos
                  </Link>
                  <Link
                    to="/admin/funcionalidades-grupos"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/funcionalidades-grupos'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <UserSquare className="w-3 h-3 mr-2" />
                    Permisos de Grupos
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/admin/users"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/users'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </Link>
            <Link
              to="/admin/departments"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/departments'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Departamentos
            </Link>
            <Link
              to="/admin/user-groups"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/user-groups'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <UserSquare className="w-4 h-4 mr-2" />
              Grupos
            </Link>
            <Link
              to="/admin/assistants"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/assistants'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Bot className="w-4 h-4 mr-2" />
              Asistentes
            </Link>
            <Link
              to="/admin/openai-models"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/openai-models'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Database className="w-4 h-4 mr-2" />
              Modelos de asistentes
            </Link>
            <Link
              to="/admin/assistant-permissions"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/assistant-permissions'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Lock className="w-4 h-4 mr-2" />
              Permisos de Asistentes
            </Link>
            <Link
              to="/admin/api-configurations"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/api-configurations'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Key className="w-4 h-4 mr-2" />
              APIs
            </Link>

            {/* Web Research Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Web Research
            </div>

            <div className="mb-1">
              <button
                onClick={() => setWebResearchOpen(!webResearchOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/tavily-search')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <Search className="w-4 h-4 mr-2" />
                  Web Research
                </div>
                {webResearchOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {webResearchOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/tavily-search"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tavily-search'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Search className="w-3 h-3 mr-2" />
                    Búsqueda
                  </Link>
                  <Link
                    to="/admin/tavily-search/results"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tavily-search/results'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <List className="w-3 h-3 mr-2" />
                    Resultados
                  </Link>
                  <Link
                    to="/admin/tavily-search/config"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tavily-search/config'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Settings className="w-3 h-3 mr-2" />
                    Configuración
                  </Link>
                </div>
              )}
            </div>

            {/* Web Extract Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Web Extract
            </div>

            <div className="mb-1">
              <button
                onClick={() => setWebExtractOpen(!webExtractOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/tavily-extract')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Web Extract
                </div>
                {webExtractOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {webExtractOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/tavily-extract"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tavily-extract'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <FileText className="w-3 h-3 mr-2" />
                    Extracción
                  </Link>
                  <Link
                    to="/admin/chunks"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/chunks'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <List className="w-3 h-3 mr-2" />
                    Chunking
                  </Link>
                  <Link
                    to="/admin/tavily-extract/config"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tavily-extract/config'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Settings className="w-3 h-3 mr-2" />
                    Configuración
                  </Link>
                  <Link
                    to="/admin/tipos-embeddings"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/tipos-embeddings'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Database className="w-3 h-3 mr-2" />
                    Tipos Embeddings
                  </Link>
                </div>
              )}
            </div>

            {/* Knowledge Base Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              Base de Conocimiento
            </div>

            <div className="mb-1">
              <button
                onClick={() => setKnowledgeBaseOpen(!knowledgeBaseOpen)}
                className={`flex items-center justify-between w-full p-2 rounded-lg text-xs ${
                  location.pathname.startsWith('/admin/vector-store') ||
                  location.pathname.startsWith('/admin/pdf-files')
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 hover:bg-white'
                }`}
              >
                <div className="flex items-center">
                  <Database className="w-4 h-4 mr-2" />
                  Gestión de Documentos
                </div>
                {knowledgeBaseOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {knowledgeBaseOpen && (
                <div className="ml-6 space-y-1 mt-1">
                  <Link
                    to="/admin/pdf-files"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/pdf-files'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <FilePdf className="w-3 h-3 mr-2" />
                    Archivos PDF
                  </Link>
                  <Link
                    to="/admin/vector-store"
                    className={`flex items-center p-2 rounded-lg text-xs ${
                      location.pathname === '/admin/vector-store'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-white'
                    }`}
                  >
                    <Box className="w-3 h-3 mr-2" />
                    Tienda de Vectores
                  </Link>
                </div>
              )}
            </div>

            {/* General Section */}
            <div className="py-2 px-3 mt-2 mb-1 text-xs font-medium text-gray-500 uppercase">
              General
            </div>
            <Link
              to="/admin/conversations"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/conversations'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Conversaciones
            </Link>
            <Link
              to="/admin/news"
              className={`flex items-center p-2 rounded-lg mb-1 text-xs ${
                location.pathname === '/admin/news'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-white'
              }`}
            >
              <Newspaper className="w-4 h-4 mr-2" />
              Noticias
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}